import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import Logo from './Logo'
import { consumeAuthReturnTo, isSafeInternalPath } from '../auth/authReturnTo'
import { usePortalAuth } from '../auth/authContexts'
import { isDevMode } from '../utils/devMode'
import {
  isAuth0EmailVerificationRequiredError,
  isAuth0EmailVerificationRequiredFromUnknown,
  readAuth0CallbackErrorFromSearch,
  storeEmailVerificationRequiredNotice,
} from '../utils/auth0EmailVerification'
import { markPortalReturningUser } from '../utils/portalReturningUser'

function useEmailVerificationBlockedOnCallback(auth0Error?: unknown): boolean {
  const { search } = useLocation()
  const fromUrl = readAuth0CallbackErrorFromSearch(search || window.location.search)
  return (
    isAuth0EmailVerificationRequiredError({
      error: fromUrl.error,
      errorDescription: fromUrl.errorDescription,
    }) || isAuth0EmailVerificationRequiredFromUnknown(auth0Error)
  )
}

function RedirectHandlerCore({
  auth0Error,
  returnTo,
}: {
  auth0Error?: unknown
  returnTo?: string
}) {
  const { isAuthenticated, isLoading } = usePortalAuth()
  const navigate = useNavigate()
  const emailVerificationBlocked = useEmailVerificationBlockedOnCallback(auth0Error)

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (isAuthenticated) {
      markPortalReturningUser()
      sessionStorage.setItem('club4x4_visited_confirm_details', 'true')
      const destination = isSafeInternalPath(returnTo) ? returnTo : '/dashboard'
      navigate(destination, { replace: true })
      return
    }
    if (emailVerificationBlocked) {
      storeEmailVerificationRequiredNotice()
    }
    navigate('/signin', { replace: true })
  }, [emailVerificationBlocked, isAuthenticated, isLoading, navigate, returnTo])

  return (
    <div className="min-h-screen bg-background-brand-primary flex items-center justify-center">
      <Logo spinning={true} />
    </div>
  )
}

function RedirectHandlerProduction() {
  const { error } = useAuth0()
  const returnTo = useMemo(() => consumeAuthReturnTo(), [])
  return <RedirectHandlerCore auth0Error={error} returnTo={returnTo} />
}

/**
 * OAuth redirect landing route (`/auth/redirect`).
 * Auth0 completes the code exchange before this runs; we route by session state.
 */
export default function RedirectHandler() {
  if (isDevMode()) {
    return <RedirectHandlerCore />
  }
  return <RedirectHandlerProduction />
}
